import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { TrendingDown, Shield, Building, Radar } from 'lucide-react';

export default function InvestmentChart() {
  const [animatedData, setAnimatedData] = useState([]);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [tooltipData, setTooltipData] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  // Refs para tooltip/containers
  const innerRefDesktop = useRef(null);
  const innerRefMobile  = useRef(null);
  const tooltipRef      = useRef(null);

  // Tamanho real do tooltip (medido após render)
  const [tooltipSize, setTooltipSize] = useState({
    w: 140, // fallback seguro
    h: 44   // fallback seguro
  });

  const investmentData = [
    { year: 2010, value: 2.283 }, { year: 2011, value: 2.334 }, { year: 2012, value: 3.637 },
    { year: 2013, value: 4.439 }, { year: 2014, value: 5.056 }, { year: 2015, value: 4.271 },
    { year: 2016, value: 2.746 }, { year: 2017, value: 2.171 }, { year: 2018, value: 2.437 },
    { year: 2019, value: 2.174 }, { year: 2020, value: 2.274 }, { year: 2021, value: 1.413 },
    { year: 2022, value: 0.944 }, { year: 2023, value: 0.672 }, { year: 2024, value: 1.129 }
  ];

  const CHART_HEIGHT = 320; // px
  const maxValue = Math.max(...investmentData.map(d => d.value));

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setAnimatedData(investmentData), 400);
    return () => clearTimeout(t);
  }, []);

  const getBarHeight = (value) => (value / maxValue) * (CHART_HEIGHT - 40);

  function positionTooltip(item, index, e) {
    const activeRef = isMobile ? innerRefMobile : innerRefDesktop;
    if (!activeRef.current) return;

    const barRect   = e.currentTarget.getBoundingClientRect();
    const innerRect = activeRef.current.getBoundingClientRect();

    // posição base (relativa ao container)
    const baseX = (barRect.left - innerRect.left) + (barRect.width / 2);
    const baseY = (barRect.top  - innerRect.top)  - 10;

    // limites com base no tamanho do tooltip e padding
    const PADDING = 8;
    const tw = tooltipSize.w;
    const th = tooltipSize.h;

    // Horizontal: manter o centro do tooltip visível dentro do container
    const minX = PADDING + tw / 2;
    const maxX = innerRect.width - PADDING - tw / 2;
    const clampedX = Math.max(minX, Math.min(baseX, maxX));

    // Vertical: como usamos translate(-50%, -100%), o ponto (x,y) é a base inferior do tooltip
    // Garantir que topo do tooltip (y - th) >= PADDING => y >= PADDING + th
    const minY = PADDING + th;
    const clampedY = Math.max(minY, baseY);

    setHoveredIndex(index);
    setTooltipData({
      year: item.year,
      value: item.value,
      x: clampedX,
      y: clampedY
    });
  }

  const onEnterOrTouch = (item, index) => (e) => positionTooltip(item, index, e);
  const onLeave = () => { setHoveredIndex(null); setTooltipData(null); };

  // Re-medir o tooltip quando ele aparece no mobile e re-clampar se necessário
  useEffect(() => {
    if (!tooltipData || !tooltipRef.current || !isMobile || !innerRefMobile.current) return;

    const rect = tooltipRef.current.getBoundingClientRect();
    const innerRect = innerRefMobile.current.getBoundingClientRect();

    // Se a medida real diferir do fallback, atualizar e re-clampar
    if (Math.abs(rect.width - tooltipSize.w) > 2 || Math.abs(rect.height - tooltipSize.h) > 2) {
      const PADDING = 8;
      const tw = rect.width;
      const th = rect.height;

      const minX = PADDING + tw / 2;
      const maxX = innerRect.width - PADDING - tw / 2;
      const minY = PADDING + th;

      setTooltipSize({ w: tw, h: th });

      setTooltipData(prev => prev ? {
        ...prev,
        x: Math.max(minX, Math.min(prev.x, maxX)),
        y: Math.max(minY, prev.y)
      } : prev);
    }
  }, [tooltipData, isMobile]); // roda quando tooltip aparece/atualiza no mobile

  // --- Dimensões mobile (1 barra por ano, scroll horizontal) ---
  const BAR_W_MOBILE = 18;
  const GAP_BETWEEN_GROUPS = 12;
  const GROUP_W_MOBILE = BAR_W_MOBILE + GAP_BETWEEN_GROUPS;
  const contentWidthMobile = (investmentData.length * GROUP_W_MOBILE) + 16; // padding

  return (
    <div className="space-y-8">
      <Card className="w-full">
        <CardHeader>
          <CardTitle className="text-2xl font-bold text-center">
            Investimento Público Direto em Aeroportos (R$ bi)
          </CardTitle>
          <p className="text-center text-gray-600">
            Valores anuais em bilhões de reais (correntes)
          </p>
        </CardHeader>
        <CardContent className="p-6">
          {isMobile ? (
            // ----- MOBILE: scroll horizontal -----
            <div className="overflow-x-auto no-scrollbar">
              <div
                ref={innerRefMobile}
                className="relative flex items-end px-4"
                style={{ height: CHART_HEIGHT, width: contentWidthMobile }}
              >
                {investmentData.map((item, index) => (
                  <div
                    key={item.year}
                    className="flex flex-col items-center justify-end"
                    style={{ width: GROUP_W_MOBILE }}
                  >
                    <div
                      className={`rounded-t transition-all duration-500 cursor-pointer
                                  ${hoveredIndex === index ? 'bg-sky-600' : 'bg-sky-500'}`}
                      style={{
                        width: BAR_W_MOBILE,
                        height: `${animatedData[index] ? getBarHeight(item.value) : 0}px`,
                        transitionDelay: `${index * 40}ms`,
                        transform: hoveredIndex === index ? 'translateY(-2px)' : 'translateY(0)',
                        transformOrigin: 'bottom'
                      }}
                      onTouchStart={onEnterOrTouch(item, index)}
                      onMouseEnter={onEnterOrTouch(item, index)}
                      onMouseLeave={onLeave}
                    />
                    <span className="text-[10px] mt-1 text-gray-500">{String(item.year).slice(2)}</span>
                  </div>
                ))}

                {tooltipData && (
                  <div
                    ref={tooltipRef}
                    className="absolute z-10 bg-gray-900 text-white px-3 py-2 rounded-lg shadow-lg pointer-events-none transition-all duration-200 whitespace-nowrap"
                    style={{ left: tooltipData.x, top: tooltipData.y, transform: 'translate(-50%,-100%)' }}
                  >
                    <div className="text-sm font-semibold">{tooltipData.year}</div>
                    <div className="text-xs">R$ {tooltipData.value.toLocaleString('pt-BR')} bilhões</div>
                 
                  </div>
                )}
              </div>
            </div>
          ) : (
            // ----- DESKTOP: grid responsiva, tooltip relativo ao container -----
            <div className="h-80 px-4 relative" ref={innerRefDesktop}>
              <div className="flex items-end justify-between h-full gap-2">
                {investmentData.map((item, index) => (
                  <div key={item.year} className="flex-1 flex flex-col items-center justify-end h-full">
                    <div
                      className={`w-full max-w-10 rounded-t transition-all duration-500 cursor-pointer
                                  ${hoveredIndex === index ? 'bg-sky-600' : 'bg-sky-500'}`}
                      style={{
                        height: `${animatedData[index] ? getBarHeight(item.value) : 0}px`,
                        transitionDelay: `${index * 50}ms`,
                        transform: hoveredIndex === index ? 'scaleY(1.05)' : 'scaleY(1)',
                        transformOrigin: 'bottom'
                      }}
                      onMouseEnter={onEnterOrTouch(item, index)}
                      onMouseLeave={onLeave}
                    />
                    <span className="text-xs mt-2 text-gray-500">{item.year}</span>
                  </div>
                ))}
              </div>

              {tooltipData && (
                <div
                  className="absolute z-10 bg-gray-900 text-white px-3 py-2 rounded-lg shadow-lg pointer-events-none transition-all duration-200"
                  style={{ left: tooltipData.x, top: tooltipData.y, transform: 'translate(-50%,-100%)' }}
                >
                  <div className="text-sm font-semibold">{tooltipData.year}</div>
                  <div className="text-xs">R$ {tooltipData.value.toLocaleString('pt-BR')} bilhões</div>
  
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Cards analíticos (mantidos) */}
      <Card className="bg-gradient-to-br from-sky-50 to-blue-100">
        <CardHeader>
          <CardTitle className="text-xl font-bold text-sky-900 flex items-center gap-2">
            <TrendingDown className="w-6 h-6" />
            Transformação do Modelo de Investimento
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="bg-white/60 p-4 rounded-lg">
            <h4 className="font-semibold text-sky-800 mb-2">Redução do Investimento Público Direto</h4>
            <p className="text-sm text-gray-700">
              O investimento público direto em aeroportos reduziu significativamente desde 2014, refletindo a transição
              para o modelo de concessões privadas. De R$ 5,05 bilhões em 2014, os aportes caíram para R$ 1,13 bilhão em 2024.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            <div className="p-4 bg-white/60 rounded-lg">
              <div className="flex items-center gap-3 mb-2">
                <Shield className="w-8 h-8 text-blue-600 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-blue-800">SISCEAB (R$ 840 mi)</h4>
                  <p className="text-sm text-gray-700">75% dos investimentos para o Sistema de Controle do Espaço Aéreo Brasileiro</p>
                </div>
              </div>
            </div>

            <div className="p-4 bg-white/60 rounded-lg">
              <div className="flex items-center gap-3 mb-2">
                <Building className="w-8 h-8 text-green-600 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-green-800">INFRAERO (R$ 188 mi)</h4>
                  <p className="text-sm text-gray-700">17% para adequações de infraestrutura aeroportuária e aviação regional</p>
                </div>
              </div>
            </div>

            <div className="p-4 bg-white/60 rounded-lg">
              <div className="flex items-center gap-3 mb-2">
                <Radar className="w-8 h-8 text-purple-600 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-purple-800">Aeroportos Regionais</h4>
                  <p className="text-sm text-gray-700">Foco no Novo PAC com investimentos em Coari/AM, Dourado/MS e Santa Rosa/RS</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white/60 p-4 rounded-lg">
            <h4 className="font-semibold text-sky-800 mb-2">Modelo de Concessões: Nova Era do Setor</h4>
            <p className="text-sm text-gray-700">
              Entre 2011 e 2022, foram realizadas 7 rodadas de concessões que transferiram 59 aeroportos para a gestão privada.
              Esse modelo consolidou-se como estratégia principal, com as concessões respondendo por mais de 90% do tráfego de
              passageiros e 98% do transporte de cargas, reduzindo a necessidade de aportes públicos diretos.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
